import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { CopyResultButton } from "@/components/CopyResultButton";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function PercentageCalculator() {
  const [value, setValue] = useState("");
  const [total, setTotal] = useState("");
  const [percentage, setPercentage] = useState("");
  const [increase, setIncrease] = useState("");
  const [result, setResult] = useState<number | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Math", url: "https://calclab.app/math" },
      { name: "Percentage Calculator", url: "https://calclab.app/calculator/percentage" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Percentage Calculator",
      "Calculate percentages, increases, decreases, and discounts. Free online percentage calculator with multiple calculation modes.",
      "https://calclab.app/calculator/percentage",
      lang
    )
  ]);

  const calculatePercentage = () => {
    const v = parseFloat(value);
    const t = parseFloat(total);

    if (isNaN(v) || isNaN(t) || t === 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers.",
        variant: "destructive",
      });
      return;
    }

    const percent = (v / t) * 100;
    setResult(percent);
    toast({
      title: "Calculation Complete",
      description: `${v} is ${percent.toFixed(2)}% of ${t}`,
    });
  };

  const calculatePercentOf = () => {
    const p = parseFloat(percentage);
    const t = parseFloat(total);

    if (isNaN(p) || isNaN(t)) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers.",
        variant: "destructive",
      });
      return;
    }

    const res = (p / 100) * t;
    setResult(res);
    toast({
      title: "Calculation Complete",
      description: `${p}% of ${t} is ${res.toFixed(2)}`,
    });
  };

  const calculateIncrease = () => {
    const v = parseFloat(value);
    const inc = parseFloat(increase);

    if (isNaN(v) || isNaN(inc)) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers.",
        variant: "destructive",
      });
      return;
    }

    const res = v * (1 + inc / 100);
    setResult(res);
    toast({
      title: "Calculation Complete",
      description: `${v} increased by ${inc}% is ${res.toFixed(2)}`,
    });
  };

  const reset = () => {
    setValue("");
    setTotal("");
    setPercentage("");
    setIncrease("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Percentage Calculator" 
      calculatorPath="/calculator/percentage"
      category="math"
      subcategory="Basic Math"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Percentage Calculator</CardTitle>
            <CardDescription>
              Calculate percentages, increases, and decreases
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs defaultValue="whatPercent">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="whatPercent">What %?</TabsTrigger>
                <TabsTrigger value="percentOf">% of</TabsTrigger>
                <TabsTrigger value="increase">Increase/Decrease</TabsTrigger>
              </TabsList>

              <TabsContent value="whatPercent" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="value">Value</Label>
                  <Input
                    id="value"
                    type="number"
                    step="any"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="What is..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="total">Total</Label>
                  <Input
                    id="total"
                    type="number"
                    step="any"
                    value={total}
                    onChange={(e) => setTotal(e.target.value)}
                    placeholder="...out of"
                  />
                </div>
                <Button onClick={calculatePercentage} className="w-full">Calculate</Button>
              </TabsContent>

              <TabsContent value="percentOf" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="percentage">Percentage</Label>
                  <Input
                    id="percentage"
                    type="number"
                    step="any"
                    value={percentage}
                    onChange={(e) => setPercentage(e.target.value)}
                    placeholder="What % (e.g., 25)"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="totalPercent">Of Value</Label>
                  <Input
                    id="totalPercent"
                    type="number"
                    step="any"
                    value={total}
                    onChange={(e) => setTotal(e.target.value)}
                    placeholder="Of what number"
                  />
                </div>
                <Button onClick={calculatePercentOf} className="w-full">Calculate</Button>
              </TabsContent>

              <TabsContent value="increase" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="valueIncrease">Original Value</Label>
                  <Input
                    id="valueIncrease"
                    type="number"
                    step="any"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="Starting value"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="increase">Increase % (use negative for decrease)</Label>
                  <Input
                    id="increase"
                    type="number"
                    step="any"
                    value={increase}
                    onChange={(e) => setIncrease(e.target.value)}
                    placeholder="e.g., 20 or -15"
                  />
                </div>
                <Button onClick={calculateIncrease} className="w-full">Calculate</Button>
              </TabsContent>
            </Tabs>

            <Button onClick={reset} variant="outline" className="w-full">Reset All</Button>

            {result !== null && (
              <>
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-start justify-between gap-4">
                      <p className="text-3xl font-bold break-all overflow-wrap-anywhere flex-1">{result.toFixed(2)}</p>
                      <CopyResultButton value={result.toFixed(2)} />
                    </div>
                  </CardContent>
                </Card>

                <CalculatorSuperCalcCTA config={calculatorCTAs["percentage"]} trigger={result} />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
